import React, { useEffect, useState, useRef } from 'react'
import Swal from 'sweetalert2';
import { addDoc, collection, doc, getDoc, getDocs, onSnapshot, orderBy, query, Timestamp, updateDoc, where } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import "../../App.css";
import moment from 'moment';
import { useReactToPrint } from 'react-to-print';
import Menu from '../../panel/Menu';
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Divider
} from "@mui/material";


function Paymodewise() {

    const Toast = Swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true
    });

    const printRef = useRef();

    const [loading, setloading] = useState(false);
    const [fromDate, setfromDate] = useState("");
    const [toDate, settoDate] = useState("");
    const [total, settotal] = useState(0);
    const [paymodes, setpaymodes] = useState([]);
    const [propertyId, setPropertyId] = useState(null);
    const [userData, setUserData] = useState(null);

    /* 🔐 AUTH + PROPERTY FETCH */
    useEffect(() => {
        const unsub = auth.onAuthStateChanged(async (user) => {
            if (!user) return;

            setUserData(user);

            const roleSnap = await getDoc(doc(db, "authroles", user.uid));
            if (roleSnap.exists()) {
                setPropertyId(roleSnap.data().propertyId);
            } else {
                Toast.fire({ icon: "error", title: "Property not assigned" });
            }
        });

        return () => unsub();
    }, []);

    /* 📊 GET REPORT */
    const getReport = async () => {

        if (!fromDate || !toDate) {
            Toast.fire({ icon: "error", title: "Please select From & To Date" });
            return;
        }

        if (!propertyId) {
            Toast.fire({ icon: "error", title: "Property not assigned" });
            return;
        }

        setloading(false);
        setpaymodes([]);
        settotal(0);

        const startDate = new Date(fromDate);
        startDate.setHours(0, 0, 0, 0);

        const endDate = new Date(toDate);
        endDate.setDate(endDate.getDate() + 1);

        try {
            /* ✅ FETCH PAYMENT MODES */
            const paymentModesSnap = await getDocs(
                query(
                    collection(db, "paymentmodes"),
                    where("propertyId", "==", propertyId)
                )
            );

            const paymentModes = paymentModesSnap.docs.map(doc => ({
                id: doc.id,
                paymode: doc.data().paymode
            }));

            /* 🔁 PARALLEL QUERIES */
            const paymode1Queries = paymentModes.map(mode =>
                getDocs(query(
                    collection(db, "payhistory"),
                    where("propertyId", "==", propertyId),
                    where("paymode1", "==", mode.id),
                    where("createdAt", ">=", Timestamp.fromDate(startDate)),
                    where("createdAt", "<", Timestamp.fromDate(endDate))
                ))
            );

            const paymode2Queries = paymentModes.map(mode =>
                getDocs(query(
                    collection(db, "payhistory"),
                    where("propertyId", "==", propertyId),
                    where("paymode2", "==", mode.id),
                    where("createdAt", ">=", Timestamp.fromDate(startDate)),
                    where("createdAt", "<", Timestamp.fromDate(endDate))
                ))
            );

            const [snap1, snap2] = await Promise.all([
                Promise.all(paymode1Queries),
                Promise.all(paymode2Queries)
            ]);

            let grandTotal = 0;

            const reportData = paymentModes.map((mode, index) => {
                const total1 = snap1[index].docs.reduce(
                    (s, d) => s + (Number(d.data().amount1) || 0), 0
                );

                const total2 = snap2[index].docs.reduce(
                    (s, d) => s + (Number(d.data().amount2) || 0), 0
                );

                const totalAmount = total1 + total2;
                grandTotal += totalAmount;

                return {
                    paymode: mode.paymode,
                    totalamount: totalAmount
                };
            });

            setpaymodes(reportData);
            settotal(grandTotal);
            setloading(true);

        } catch (err) {
            console.error(err);
            Toast.fire({ icon: "error", title: "Error loading report" });
        }
    };

    /* 🖨 PRINT */
    const handlePrint = () => {
        window.print();
    };


    const handleFromDate = (e) => {
        setfromDate(e.target.value);
        setloading(false);
    };

    const handleToDate = (e) => {
        settoDate(e.target.value);
        setloading(false);
    };

    // UI JSX here...

    return (
        <div>
            <Menu />
            <div className="content-wrapper">
                <Box sx={{ px: 1, p: 3 }}>

                    <Typography variant="h6" fontWeight="bold">
                        || Paymode Wise Report ||
                    </Typography>

                    {/* FILTER CARD */}
                    <Card className='mt-2'>
                        <CardContent>
                            <Grid className="row" spacing={2} alignItems="center">
                                <Grid className="col-sm-3" sm={4}>
                                    <TextField
                                        fullWidth
                                        size='small'
                                        type="date"
                                        label="From Date"
                                        value={fromDate}
                                        onChange={handleFromDate}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>

                                <Grid className="col-sm-3" sm={4}>
                                    <TextField
                                        fullWidth
                                        size='small'
                                        type="date"
                                        label="To Date"
                                        value={toDate}
                                        onChange={handleToDate}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>

                                <Grid className="col-sm-3" sm={4}>
                                    <Button
                                        fullWidth
                                        size='small'
                                        variant="contained"
                                        color="info"
                                        onClick={getReport}
                                        sx={{ height: "40px" }}
                                    >
                                        Get Report
                                    </Button>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* REPORT */}
                    {loading && (
                        <>
                            <Card
                                elevation={3}
                                className="print-area"
                                ref={printRef}
                                sx={{ borderRadius: 2 }}
                            >
                                <CardContent>
                                    <Divider sx={{ mb: 2 }} />

                                    {/* DATE HEADER */}
                                    <Grid className="row" spacing={2} mb={2}>
                                        <Grid className="col-sm-4" sm={4} textAlign="center">
                                            <Typography variant="subtitle2">
                                                From Date : {moment(fromDate).format("DD/MM/YYYY")}
                                            </Typography>
                                        </Grid>

                                        <Grid className="col-sm-4" sm={4} textAlign="center">
                                            <Typography variant="h6" fontWeight="bold">
                                                PAYMODE WISE REPORT
                                            </Typography>
                                        </Grid>

                                        <Grid className="col-sm-4" sm={4} textAlign="center">
                                            <Typography variant="subtitle2">
                                                To Date : {moment(toDate).format("DD/MM/YYYY")}
                                            </Typography>
                                        </Grid>
                                    </Grid>

                                    {/* TABLE */}
                                    <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ backgroundColor: "#E0FCFE" }}>
                                                    <TableCell><b>Paymode</b></TableCell>
                                                    <TableCell align="right"><b>Amount</b></TableCell>
                                                </TableRow>
                                            </TableHead>

                                            <TableBody>
                                                {paymodes.map((item, index) => (
                                                    <TableRow key={index}>
                                                        <TableCell>{item.paymode}</TableCell>
                                                        <TableCell align="right">
                                                            {item.totalamount}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}

                                                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                                                    <TableCell><b>Total</b></TableCell>
                                                    <TableCell align="right">
                                                        <b>{total}</b>
                                                    </TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </CardContent>
                            </Card>

                            {/* PRINT BUTTON */}
                            <Box textAlign="center" mt={3}>
                                <Button
                                    variant="contained"
                                    color="success"
                                    size="large"
                                    onClick={handlePrint}
                                >
                                    Print Report
                                </Button>
                            </Box>
                        </>
                    )}
                </Box>
            </div>
        </div >

    )
}

export default Paymodewise
